// src/pages/Campaign/CampaignDetails.tsx

import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Layout/Sidebar";
import Header from "../../components/Layout/Header";
import "../../assets/styles/campaign-details.css";
import commonService from "../../services/otherServices";
import { useParams } from "react-router-dom";
import Toast from "../../components/Common/Toast";
import { getErrorMessage } from "../../services/apiClient";
import { formatIST } from "../../utils/dateHelpers";
import { RotateCcw, RefreshCw, Download, Clock, Play, Square, ShieldCheck, Globe } from "lucide-react";

const ITEMS_PER_PAGE = 10;

/**
 * "12m 17s" / "1h 5m" / "42s" style duration between two crawl timestamps.
 * Returns "-" until both a start and an end are available.
 */
const formatCrawlDuration = (
  start?: string | null,
  end?: string | null
): string => {
  if (!start || !end) return "-";

  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();

  if (isNaN(startMs) || isNaN(endMs) || endMs < startMs) return "-";

  const totalSeconds = Math.floor((endMs - startMs) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const toolbarButtonStyle = (
  variant: "outline" | "primary",
  disabled: boolean
): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  padding: "9px 16px",
  borderRadius: "10px",
  fontWeight: 600,
  fontSize: "13px",
  border: variant === "outline" ? "1px solid #e5e7eb" : "none",
  background: variant === "primary" ? "#2563eb" : "#fff",
  color:
    variant === "primary"
      ? "#fff"
      : disabled
        ? "#9ca3af"
        : "#dc2626",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
  boxShadow:
    variant === "primary"
      ? "0 4px 10px rgba(37,99,235,0.25)"
      : "none",
  whiteSpace: "nowrap",
  transition: "background 0.15s ease, box-shadow 0.15s ease",
});

const CampaignDetails: React.FC = () => {

  const { id } = useParams();

  const [loading, setLoading] = useState(true);

  const [campaign, setCampaign] = useState<any>(null);

  const [leads, setLeads] = useState<any[]>([]);

  const [sequence, setSequence] = useState<any[]>([]);

  const [page, setPage] = useState(1);

  const [openSequenceId, setOpenSequenceId] =
    useState<number | null>(null);

  const [isRetryingScrapes, setIsRetryingScrapes] =
    useState(false);

  const [isRetryingVerifications, setIsRetryingVerifications] =
    useState(false);

  const [isDownloadingEmails, setIsDownloadingEmails] =
    useState(false);

  const [sortConfig, setSortConfig] = useState<{
    key: "lead_status" | "verification" | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });

  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // ==========================================
  // FETCH DETAILS
  // ==========================================

  useEffect(() => {

    if (id) {
      fetchCampaignDetails();
    }

  }, [id]);

  // Keeps per-lead verification progress bars live without a full-page
  // reload flash - the verifier's own result-fetch worker polls every
  // 30s (see app/worker/email_verify_scheduler.py), so matching that
  // cadence here means this never shows stale progress for long.
  useEffect(() => {

    if (!id) return;

    const interval = setInterval(() => {
      fetchCampaignDetails(true);
    }, 30000);

    return () => clearInterval(interval);

  }, [id]);

  const fetchCampaignDetails = async (silent: boolean = false) => {

    try {

      if (!silent) {
        setLoading(true);
      }

      const res =
        await commonService.getCampaignDetails(
          Number(id)
        );

      const data = res.data?.data;

      setCampaign(data?.campaign || null);

      setLeads(data?.leads || []);

      const formattedSequence =
        (data?.sequence || []).map((item: any) => {

          let parsedTemplate = {
            subject: "",
            body: "",
          };

          try {
            if (item.email_template) {

              parsedTemplate =
                typeof item.email_template === "string"
                  ? JSON.parse(item.email_template)
                  : item.email_template;
            }

          } catch (err) {
            console.error(err);
          }

          return {
            ...item,
            subject: parsedTemplate.subject || "",
            body: parsedTemplate.body || "",
          };
        });

      setSequence(formattedSequence);

    } catch (err) {

      console.error(
        "Failed to fetch campaign details",
        err
      );

    } finally {

      setLoading(false);
    }
  };

  // ==========================================
  // RETRY FAILED SCRAPES
  // ==========================================

  const failedLeads = leads.filter(
    (lead) => lead.lead_status === "error"
  );

  const handleRetryFailedScrapes = async () => {

    if (failedLeads.length === 0) {

      setToast({
        type: "error",
        message: "No failed domains to reset",
      });

      return;
    }

    setIsRetryingScrapes(true);

    try {

      const results = await Promise.allSettled(
        failedLeads.map((lead) =>
          commonService.retryLeadScrape(lead.lead_id)
        )
      );

      const failures = results.filter(
        (r) => r.status === "rejected"
      );

      if (failures.length > 0) {

        setToast({
          type: "error",
          message: `Reset ${results.length - failures.length} of ${results.length} domains. ${failures.length} failed.`,
        });

      } else {

        setToast({
          type: "success",
          message: `Reset ${results.length} failed domain${results.length > 1 ? "s" : ""} for re-scraping`,
        });
      }

      fetchCampaignDetails();

    } catch (err) {

      console.error(
        "Failed to retry scraping",
        err
      );

      setToast({
        type: "error",
        message: getErrorMessage(err, "Failed to reset domains for re-scraping"),
      });

    } finally {

      setIsRetryingScrapes(false);
    }
  };

  // ==========================================
  // RETRY FAILED VERIFICATIONS
  // ==========================================

  const failedVerificationLeads = leads.filter(
    (lead) => lead.email_link_status === "error"
  );

  const handleRetryFailedVerifications = async () => {

    if (failedVerificationLeads.length === 0) {

      setToast({
        type: "error",
        message: "No failed verifications to retry",
      });

      return;
    }

    setIsRetryingVerifications(true);

    try {

      // NOTE: assumes each lead exposes its own verification-record id as
      // `email_link_id` (mirroring `email_link_status`) — falls back to
      // `lead_id` if that field isn't present. Confirm against the live
      // API response and drop the fallback once verified.
      const results = await Promise.allSettled(
        failedVerificationLeads.map((lead) =>
          commonService.retryEmailVerification(
            lead.email_link_id ?? lead.lead_id
          )
        )
      );

      const failures = results.filter(
        (r) => r.status === "rejected"
      );

      if (failures.length > 0) {

        setToast({
          type: "error",
          message: `Retried ${results.length - failures.length} of ${results.length} verifications. ${failures.length} failed.`,
        });

      } else {

        setToast({
          type: "success",
          message: `Retried ${results.length} failed verification${results.length > 1 ? "s" : ""}`,
        });
      }

      fetchCampaignDetails();

    } catch (err) {

      console.error(
        "Failed to retry verification",
        err
      );

      setToast({
        type: "error",
        message: getErrorMessage(err, "Failed to retry verification"),
      });

    } finally {

      setIsRetryingVerifications(false);
    }
  };

  // ==========================================
  // DOWNLOAD EMAILS CSV
  // ==========================================

  const handleDownloadEmails = async () => {

    setIsDownloadingEmails(true);

    try {

      await commonService.downloadCampaignEmails(
        Number(id)
      );

    } catch (err) {

      console.error(
        "Failed to download campaign emails",
        err
      );

      setToast({
        type: "error",
        message: getErrorMessage(err, "Failed to download campaign emails"),
      });

    } finally {

      setIsDownloadingEmails(false);
    }
  };

  // ==========================================
  // SORTING
  // ==========================================

  const getVerificationStatus = (lead: any) =>
    lead.email_link_status ||
    (lead.lead_status === "completed" ? "completed" : "pending");

  const handleSort = (key: "lead_status" | "verification") => {

    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));

    setPage(1);
  };

  const renderSortIndicator = (key: "lead_status" | "verification") => {

    const isActive = sortConfig.key === key;
    const ascActive = isActive && sortConfig.direction === "asc";
    const descActive = isActive && sortConfig.direction === "desc";

    return (
      <span
        style={{
          display: "inline-flex",
          flexDirection: "column",
          marginLeft: "6px",
          fontSize: "9px",
          lineHeight: "9px",
          verticalAlign: "middle",
        }}
      >
        <span style={{ color: ascActive ? "#111827" : "#9ca3af" }}>
          ▲
        </span>
        <span style={{ color: descActive ? "#111827" : "#9ca3af" }}>
          ▼
        </span>
      </span>
    );
  };

  const sortedLeads = [...leads].sort((a, b) => {

    if (!sortConfig.key) return 0;

    const aValue =
      sortConfig.key === "lead_status"
        ? a.lead_status || ""
        : getVerificationStatus(a);

    const bValue =
      sortConfig.key === "lead_status"
        ? b.lead_status || ""
        : getVerificationStatus(b);

    const comparison = aValue.localeCompare(bValue);

    return sortConfig.direction === "asc" ? comparison : -comparison;
  });

  // ==========================================
  // PAGINATION
  // ==========================================

  const totalPages = Math.ceil(
    sortedLeads.length / ITEMS_PER_PAGE
  );

  const paginatedLeads = sortedLeads.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // ==========================================
  // LOADING
  // ==========================================

  if (loading) {

    return (
      <div className="app-container">

        <Sidebar />

        <div className="main">

          <Header />

          <div className="content">

            <div className="campaign-loading">
              Loading campaign details...
            </div>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">

      <Sidebar />

      <div className="main">

        <Header />

        <div className="content">

          {/* HEADER */}
          <div className="campaign-header-card">

            <div>

              <h2>
                Campaign Details
              </h2>

              <p className="campaign-file">
                {campaign?.file_name}
              </p>

            </div>

            <div
              className={`campaign-status status-${campaign?.status}`}
            >
              {campaign?.status}
            </div>

          </div>

          {/* CAMPAIGN INFO */}
          <div className="info-card full-card">

            <h3>
              Campaign Information
            </h3>

            <div className="info-grid">

              <div className="info-row">
                <span>Total Leads</span>
                <strong>
                  {leads.length}
                </strong>
              </div>

              {/* <div className="info-row">
                <span>Data Count</span>
                <strong>
                  {campaign?.data_count || 0}
                </strong>
              </div> */}

              <div className="info-row">
                <span>Created By</span>
                <strong>
                  {campaign?.created_by || "-"}
                </strong>
              </div>

              {/* <div className="info-row">
                <span>Sender Name</span>
                <strong>
                  {campaign?.sender_name || "-"}
                </strong>
              </div>

              <div className="info-row">
                <span>Sender Email</span>
                <strong>
                  {campaign?.sender_detail || "-"}
                </strong>
              </div> */}

              <div className="info-row">
                <span>Created At</span>
                <strong>
                  {campaign?.created_at
                    ? `${formatIST(campaign.created_at)} IST`
                    : "-"}
                </strong>
              </div>

            </div>

            {/* CRAWLING DETAILS */}
            <div className="crawling-details-bar">

              <div className="crawling-details-label">
                <div className="crawling-details-icon">
                  <Globe size={16} />
                </div>
                Crawling Details
              </div>

              <div className="crawling-divider" />

              <div className="crawling-item">
                <span className="crawling-item-label">
                  Start Time
                </span>
                <span className="crawling-item-value">
                  <span className="crawling-dot crawling-dot-start">
                    <Play size={9} fill="#fff" color="#fff" />
                  </span>
                  {campaign?.crawling_started_at
                    ? `${formatIST(campaign.crawling_started_at)} IST`
                    : "-"}
                </span>
              </div>

              <div className="crawling-divider" />

              <div className="crawling-item">
                <span className="crawling-item-label">
                  End Time
                </span>
                <span className="crawling-item-value">
                  <span className="crawling-dot crawling-dot-end">
                    <Square size={8} fill="#fff" color="#fff" />
                  </span>
                  {campaign?.crawling_completed_at
                    ? `${formatIST(campaign.crawling_completed_at)} IST`
                    : campaign?.crawling_started_at
                      ? "In Progress"
                      : "-"}
                </span>
              </div>

              <div className="crawling-duration">
                <span className="crawling-duration-value">
                  <Clock size={13} />
                  {formatCrawlDuration(
                    campaign?.crawling_started_at,
                    campaign?.crawling_completed_at
                  )}
                </span>
                <span className="crawling-duration-label">
                  Duration
                </span>
              </div>

            </div>

            {/* VERIFICATION DETAILS - separate process from crawling above;
                intentionally its own bar with its own classes, not reused
                from .crawling-details-* - and there is deliberately no
                combined/total duration anywhere in this file. */}
            <div className="verification-details-bar">

              <div className="verification-details-label">
                <div className="verification-details-icon">
                  <ShieldCheck size={18} />
                </div>
                Verification Details
              </div>

              <div className="verification-divider" />

              <div className="verification-item">
                <span className="verification-item-label">
                  Start Time
                </span>
                <span className="verification-item-value">
                  <span className="verification-dot verification-dot-start">
                    <Play size={9} fill="#fff" color="#fff" />
                  </span>
                  {campaign?.verification_started_at
                    ? `${formatIST(campaign.verification_started_at)} IST`
                    : "-"}
                </span>
              </div>

              <div className="verification-divider" />

              <div className="verification-item">
                <span className="verification-item-label">
                  End Time
                </span>
                <span className="verification-item-value">
                  <span className="verification-dot verification-dot-end">
                    <Square size={8} fill="#fff" color="#fff" />
                  </span>
                  {campaign?.verification_completed_at
                    ? `${formatIST(campaign.verification_completed_at)} IST`
                    : campaign?.verification_started_at
                      ? "In Progress"
                      : "-"}
                </span>
              </div>

              <div className="verification-duration">
                <span className="verification-duration-value">
                  <Clock size={13} />
                  {formatCrawlDuration(
                    campaign?.verification_started_at,
                    campaign?.verification_completed_at
                  )}
                </span>
                <span className="verification-duration-label">
                  Duration
                </span>
              </div>

            </div>

            {/* <div className="product-details">

              <h4>
                Product Details
              </h4>

              <pre>
                {campaign?.product_details || "-"}
              </pre>

            </div> */}

          </div>

          {/* SEQUENCE */}
          {/* <div className="info-card">

            <h3>
              Campaign Sequence
            </h3>

            <div className="sequence-wrapper">

              {sequence.map((item) => (

                <div
                  className="sequence-card"
                  key={item.id}
                >

                  <div
                    className="sequence-header"
                    onClick={() =>
                      setOpenSequenceId(
                        openSequenceId === item.id
                          ? null
                          : item.id
                      )
                    }
                  >

                    <div>

                      <strong>
                        {item.step_name}
                      </strong>

                      <p>
                        Delay:
                        {" "}
                        {item.delay_days}
                        {" "}
                        day(s)
                      </p>

                    </div>

                    <div className="sequence-right">

                      <span
                        className={`sequence-status seq-${item.status}`}
                      >
                        {item.status}
                      </span>

                      <span className="expand-icon">
                        {openSequenceId === item.id
                          ? "▲"
                          : "▼"}
                      </span>

                    </div>

                  </div>

                  {openSequenceId === item.id && (

                    <div className="sequence-body">

                      <div className="sequence-info-row">
                        <span>
                          Scheduled At:
                        </span>

                        <strong>
                          {item.scheduled_at
                            ? `${formatIST(item.scheduled_at)} IST`
                            : "Yet to be scheduled"}
                        </strong>
                      </div>

                      <div className="sequence-info-row">
                        <span>
                          Sent At:
                        </span>

                        <strong>
                          {item.sent_at
                            ? `${formatIST(item.sent_at)} IST`
                            : "Yet to be sent"}
                        </strong>
                      </div>

                      <div className="template-block">

                        <h4>
                          Subject
                        </h4>

                        <p>
                          {item.subject || "--"}
                        </p>

                      </div>

                      <div className="template-block">

                        <h4>
                          Email Template
                        </h4>

                        <pre>
                          {item.body || "--"}
                        </pre>

                      </div>

                    </div>
                  )}

                </div>
              ))}

            </div>

          </div> */}

          {/* LEADS */}
          <div className="lead-section">

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
                marginBottom: "18px",
              }}
            >

              <h3 style={{ margin: 0 }}>
                Leads & Email Summary
              </h3>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >

                <button
                  onClick={handleRetryFailedScrapes}
                  disabled={isRetryingScrapes || failedLeads.length === 0}
                  style={toolbarButtonStyle("outline", failedLeads.length === 0)}
                >
                  <RotateCcw size={15} />
                  {isRetryingScrapes
                    ? "Resetting..."
                    : `Reset Failed Domains${failedLeads.length ? ` (${failedLeads.length})` : ""}`}
                </button>

                <button
                  onClick={handleRetryFailedVerifications}
                  disabled={isRetryingVerifications || failedVerificationLeads.length === 0}
                  style={toolbarButtonStyle("outline", failedVerificationLeads.length === 0)}
                >
                  <RefreshCw size={15} />
                  {isRetryingVerifications
                    ? "Retrying..."
                    : `Retry Failed Verifications${failedVerificationLeads.length ? ` (${failedVerificationLeads.length})` : ""}`}
                </button>

                <button
                  onClick={handleDownloadEmails}
                  disabled={isDownloadingEmails}
                  style={toolbarButtonStyle("primary", isDownloadingEmails)}
                >
                  <Download size={15} />
                  {isDownloadingEmails ? "Downloading..." : "Download Emails (CSV)"}
                </button>

              </div>

            </div>

            <div className="lead-table-wrapper">

              <table className="lead-table">

                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Domain</th>
                    <th
                      onClick={() => handleSort("lead_status")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      Scraping Status{renderSortIndicator("lead_status")}
                    </th>
                    <th
                      onClick={() => handleSort("verification")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      Verification{renderSortIndicator("verification")}
                    </th>
                    <th>Total</th>
                    <th>Valid</th>
                    <th>Risky</th>
                    <th>Invalid</th>
                    <th>Catch-All</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLeads.map((lead) => (

                    <tr key={lead.lead_id}>

                      <td>
                        {lead.company_name?lead.company_name:"-"}
                      </td>

                      <td>
                        <a
                          href={lead.company_domain}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {lead.company_domain}
                        </a>
                      </td>

                      <td>

                        <span
                          className={`table-badge badge-${lead.lead_status}`}
                          title={
                            lead.lead_status === "error" && (lead.email_summary?.total_emails || 0) > 0
                              ? `A later retry failed, but ${lead.email_summary.total_emails} email(s) from an earlier successful scrape are still here — see the columns to the right.`
                              : undefined
                          }
                        >
                          {lead.lead_status}
                        </span>

                        {lead.lead_status === "error" && (lead.email_summary?.total_emails || 0) > 0 && (
                          <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>
                            {lead.email_summary.total_emails} email(s) already found
                          </div>
                        )}

                      </td>

                      <td>

                        {getVerificationStatus(lead) === "inprogress" ? (

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              minWidth: "90px",
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                height: "6px",
                                borderRadius: "999px",
                                background: "#e5e7eb",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  width: `${Math.min(100, Math.max(0, lead.email_verification_progress || 0))}%`,
                                  height: "100%",
                                  background: "#2563eb",
                                  transition: "width 0.3s ease",
                                }}
                              />
                            </div>

                            <span
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {Math.round(lead.email_verification_progress || 0)}%
                            </span>
                          </div>

                        ) : (

                          <span
                            className={`table-badge badge-${getVerificationStatus(lead)}`}
                          >
                            {getVerificationStatus(lead)}
                          </span>

                        )}

                      </td>

                      <td>
                        {lead.email_summary?.total_emails || 0}
                      </td>

                      <td className="valid-count">
                        {lead.email_summary?.valid_emails || 0}
                      </td>

                      <td className="risky-count">
                        {lead.email_summary?.risky_emails || 0}
                      </td>

                      <td className="invalid-count">
                        {lead.email_summary?.invalid_emails || 0}
                      </td>

                      <td className="catch-count">
                        {lead.email_summary?.catch_all_emails || 0}
                      </td>

                    </tr>
                  ))}

                </tbody>

              </table>

            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (

              <div className="pagination">

                <button
                  disabled={page === 1}
                  onClick={() =>
                    setPage(page - 1)
                  }
                >
                  Prev
                </button>

                <span>
                  Page
                  {" "}
                  {page}
                  {" "}
                  of
                  {" "}
                  {totalPages}
                </span>

                <button
                  disabled={
                    page === totalPages
                  }
                  onClick={() =>
                    setPage(page + 1)
                  }
                >
                  Next
                </button>

              </div>
            )}

          </div>

        </div>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

    </div>
  );
};

export default CampaignDetails;